'use client';

import { AiMagicSolidIcon, Cancel01Icon, Tick01Icon } from '@terra/ui-icons';
import { Box, HStack } from '@terra/ui-styled-system';
import { IconButton, LabelDefault, MetaDefault } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';

type FindingCopilotNoteSuggestedSelectProps = {
  variant: 'suggested-select';
  beforeLabel: string;
  afterLabel: string;
  isFocused?: boolean;
  onAccept?: () => void;
  onDiscard?: () => void;
};

type FindingCopilotNoteSuggestedTextProps = {
  variant: 'suggested-text';
  isFocused?: boolean;
  onAccept?: () => void;
  onDiscard?: () => void;
};

type FindingCopilotNoteAffectedLabelProps = {
  variant: 'affected-label';
  label: string;
};

type FindingCopilotNoteAffectedTransitionProps = {
  variant: 'affected-transition';
  beforeLabel: string;
  afterLabel: string;
};

type FindingCopilotNoteGroupMessageProps = {
  variant: 'group-message';
  message: string;
  isFocused?: boolean;
  onAccept?: () => void;
  onDiscard?: () => void;
};

type FindingCopilotNoteAcceptedProps = {
  variant: 'accepted';
};

export type FindingCopilotNoteProps =
  | FindingCopilotNoteSuggestedSelectProps
  | FindingCopilotNoteSuggestedTextProps
  | FindingCopilotNoteAffectedLabelProps
  | FindingCopilotNoteAffectedTransitionProps
  | FindingCopilotNoteGroupMessageProps
  | FindingCopilotNoteAcceptedProps;

function CopilotMark() {
  return (
    <Box
      flexShrink={0}
      color="fg.accent.default"
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="3"
      h="3"
    >
      <AiMagicSolidIcon size={12} />
    </Box>
  );
}

export function FindingCopilotSuggestedActions({
  onAccept,
  onDiscard,
  acceptVariant,
}: {
  onAccept?: () => void;
  onDiscard?: () => void;
  acceptVariant: 'solid' | 'ghost';
}) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.suggestionReview',
  );

  return (
    <HStack gap={1} alignItems="center" flexShrink={0}>
      <IconButton
        type="button"
        size="3xs"
        variant="ghost"
        color="fg.neutral.default"
        aria-label={t('rejectSuggestion')}
        onClick={onDiscard}
      >
        <Cancel01Icon size={12} />
      </IconButton>
      <IconButton
        type="button"
        size="3xs"
        variant={acceptVariant}
        colorPalette={acceptVariant === 'solid' ? 'accent' : undefined}
        color={acceptVariant === 'ghost' ? 'fg.neutral.default' : undefined}
        data-accept-variant={acceptVariant}
        aria-label={t('acceptSuggestion')}
        onClick={onAccept}
        borderRadius="sm"
      >
        <Tick01Icon size={12} />
      </IconButton>
    </HStack>
  );
}

function BeforeAfterLabels({
  beforeLabel,
  afterLabel,
  strikeBefore = true,
}: {
  beforeLabel: string;
  afterLabel: string;
  strikeBefore?: boolean;
}) {
  return (
    <>
      <MetaDefault
        color={strikeBefore ? 'fg.neutral.muted' : 'fg.default'}
        textDecoration={strikeBefore ? 'line-through' : undefined}
        maxW="40%"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {beforeLabel}
      </MetaDefault>
      <MetaDefault
        color={strikeBefore ? 'fg.neutral.muted' : 'fg.default'}
        flexShrink={0}
      >
        →
      </MetaDefault>
      <MetaDefault
        color="fg.default"
        maxW="40%"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {afterLabel}
      </MetaDefault>
    </>
  );
}

export function FindingCopilotNote(props: FindingCopilotNoteProps) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.suggestionReview',
  );

  if (props.variant === 'group-message') {
    return (
      <HStack
        gap={2}
        alignItems="center"
        justifyContent="space-between"
        w="full"
        px={2}
        py={1.5}
        borderRadius="md"
        bg="bg.neutral.subtle"
      >
        <HStack gap={1.5} alignItems="center" minW={0}>
          <CopilotMark />
          <LabelDefault color="fg.default">{props.message}</LabelDefault>
        </HStack>
        <FindingCopilotSuggestedActions
          onAccept={props.onAccept}
          onDiscard={props.onDiscard}
          acceptVariant="solid"
        />
      </HStack>
    );
  }

  if (props.variant === 'accepted') {
    return (
      <HStack gap={1.5} alignItems="center" w="full">
        <Box flexShrink={0} color="fg.success" display="flex" w="3" h="3">
          <Tick01Icon size={12} />
        </Box>
        <MetaDefault color="fg.success">{t('accepted')}</MetaDefault>
      </HStack>
    );
  }

  if (props.variant === 'affected-label') {
    return (
      <HStack gap={1.5} alignItems="center" w="full" minW={0}>
        <CopilotMark />
        <MetaDefault color="fg.default">{props.label}</MetaDefault>
      </HStack>
    );
  }

  if (props.variant === 'affected-transition') {
    return (
      <HStack gap={1.5} alignItems="center" w="full" minW={0}>
        <CopilotMark />
        <BeforeAfterLabels
          beforeLabel={props.beforeLabel}
          afterLabel={props.afterLabel}
          strikeBefore={false}
        />
      </HStack>
    );
  }

  if (props.variant === 'suggested-text') {
    return (
      <HStack
        gap={1.5}
        alignItems="center"
        justifyContent="space-between"
        w="full"
      >
        <HStack gap={1.5} alignItems="center" minW={0}>
          <CopilotMark />
          <MetaDefault color="fg.default">{t('rewritten')}</MetaDefault>
        </HStack>
        <Box flex={1} minW={0} />
        <FindingCopilotSuggestedActions
          onAccept={props.onAccept}
          onDiscard={props.onDiscard}
          acceptVariant="solid"
        />
      </HStack>
    );
  }

  return (
    <HStack gap={1.5} alignItems="center" w="full" minW={0}>
      <CopilotMark />
      <BeforeAfterLabels
        beforeLabel={props.beforeLabel}
        afterLabel={props.afterLabel}
      />
      <Box flex={1} minW={0} />
      <FindingCopilotSuggestedActions
        onAccept={props.onAccept}
        onDiscard={props.onDiscard}
        acceptVariant="solid"
      />
    </HStack>
  );
}
